/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */
//'use strict';
/* exported arrayAverage, arrayMax, arrayMin, enumerateStats */

// array<function> returns the average (down to nearest int), max and min of
// an int array.
let currentTest;
function arrayAverage(array) {
  const cnt = array.length;
  let tot = 0;
  for (let i = 0; i < cnt; i++) {
    tot += array[i];
  }
  return Math.floor(tot / cnt);
}

function arrayMax(array) {
  if (array.length === 0) {
    return NaN;
  }
  return Math.max(...array);
}

function arrayMin(array) {
  if (array.length === 0) {
    return NaN;
  }
  return Math.min(...array);
}
function setTestProgress(value) {
  currentTest.setProgress(value);
}
function setActiveTest(test) {
  currentTest = test;
}
// Enumerates the new standard compliant stats using local and remote track ids.
function enumerateStats(stats, localTrackIds, remoteTrackIds) {
  // Create an object structure with all the needed stats and types that we care
  // about. This allows to map the getStats stats to other stats names.
  const statsObject = {
    audio: {
      local: {
        audioLevel: 0.0,
        bytesSent: 0,
        clockRate: 0,
        codecId: '',
        mimeType: '',
        packetsSent: 0,
        payloadType: 0,
        timestamp: 0.0,
        trackId: '',
        transportId: '',
      },
      remote: {
        audioLevel: 0.0,
        bytesReceived: 0,
        clockRate: 0,
        codecId: '',
        fractionLost: 0,
        jitter: 0,
        mimeType: '',
        packetsLost: -1,
        packetsReceived: 0,
        payloadType: 0,
        timestamp: 0.0,
        trackId: '',
        transportId: '',
      },
    },
    video: {
      local: {
        bytesSent: 0,
        clockRate: 0,
        codecId: '',
        firCount: 0,
        framesEncoded: 0,
        frameHeight: 0,
        framesSent: -1,
        frameWidth: 0,
        nackCount: 0,
        packetsSent: -1,
        payloadType: 0,
        pliCount: 0,
        qpSum: 0,
        timestamp: 0.0,
        trackId: '',
        transportId: '',
      },
      remote: {
        bytesReceived: -1,
        clockRate: 0,
        codecId: '',
        firCount: -1,
        fractionLost: 0,
        frameHeight: 0,
        framesDecoded: 0,
        framesDropped: 0,
        framesReceived: 0,
        frameWidth: 0,
        nackCount: -1,
        packetsLost: -1,
        packetsReceived: 0,
        payloadType: 0,
        pliCount: -1,
        qpSum: 0,
        timestamp: 0.0,
        trackId: '',
        transportId: '',
      },
    },
    connection: {
      availableOutgoingBitrate: 0,
      bytesReceived: 0,
      bytesSent: 0,
      consentRequestsSent: 0,
      currentRoundTripTime: 0.0,
      localCandidateId: '',
      localCandidateType: '',
      localIp: '',
      localPort: 0,
      localPriority: 0,
      localProtocol: '',
      remoteCandidateId: '',
      remoteCandidateType: '',
      remoteIp: '',
      remotePort: 0,
      remotePriority: 0,
      remoteProtocol: '',
      requestsReceived: 0,
      requestsSent: 0,
      responsesReceived: 0,
      responsesSent: 0,
      timestamp: 0.0,
      totalRoundTripTime: 0.0,
    },
  };

  // Need to find the codec, local and remote ID's first.
  if (stats) {
    stats.forEach(((report, stat) => {
      switch (report.type) {
        case 'outbound-rtp':
          if (report.hasOwnProperty('trackId')) {
            if (report.trackId.indexOf(localTrackIds.audio) !== 1 &
                localTrackIds.audio !== '') {
              statsObject.audio.local.bytesSent = report.bytesSent;
              statsObject.audio.local.codecId = report.codecId;
              statsObject.audio.local.packetsSent = report.packetsSent;
              statsObject.audio.local.timestamp = report.timestamp;
              statsObject.audio.local.trackId = report.trackId;
              statsObject.audio.local.transportId = report.transportId;
            } else if (report.trackId.indexOf(localTrackIds.video) !== 1 &
                localTrackIds.video !== '') {
              statsObject.video.local.bytesSent = report.bytesSent;
              statsObject.video.local.codecId = report.codecId;
              statsObject.video.local.firCount = report.firCount;
              statsObject.video.local.framesEncoded = report.framesEncoded;
              statsObject.video.local.framesSent = report.framesSent;
              statsObject.video.local.packetsSent = report.packetsSent;
              statsObject.video.local.pliCount = report.pliCount;
              statsObject.video.local.qpSum = report.qpSum;
              statsObject.video.local.timestamp = report.timestamp;
              statsObject.video.local.trackId = report.trackId;
              statsObject.video.local.transportId = report.transportId;
            }
          }
          break;
        case 'inbound-rtp':
          if (report.hasOwnProperty('trackId')) {
            if (report.trackId.indexOf(remoteTrackIds.audio) !== 1 &
                remoteTrackIds.audio !== '') {
              statsObject.audio.remote.bytesReceived = report.bytesReceived;
              statsObject.audio.remote.codecId = report.codecId;
              statsObject.audio.remote.fractionLost = report.fractionLost;
              statsObject.audio.remote.jitter = report.jitter;
              statsObject.audio.remote.packetsLost = report.packetsLost;
              statsObject.audio.remote.packetsReceived = report.packetsReceived;
              statsObject.audio.remote.timestamp = report.timestamp;
              statsObject.audio.remote.trackId = report.trackId;
              statsObject.audio.remote.transportId = report.transportId;
            }
            if (report.trackId.indexOf(remoteTrackIds.video) !== 1 &
                remoteTrackIds.video !== '') {
              statsObject.video.remote.bytesReceived = report.bytesReceived;
              statsObject.video.remote.codecId = report.codecId;
              statsObject.video.remote.firCount = report.firCount;
              statsObject.video.remote.fractionLost = report.fractionLost;
              statsObject.video.remote.nackCount = report.nackCount;
              statsObject.video.remote.packetsLost = report.packetsLost;
              statsObject.video.remote.packetsReceived = report.packetsReceived;
              statsObject.video.remote.pliCount = report.pliCount;
              statsObject.video.remote.qpSum = report.qpSum;
              statsObject.video.remote.timestamp = report.timestamp;
              statsObject.video.remote.trackId = report.trackId;
              statsObject.video.remote.transportId = report.transportId;
            }
          }
          break;
        case 'candidate-pair':
          if (report.hasOwnProperty('availableOutgoingBitrate')) {
            statsObject.connection.availableOutgoingBitrate =
                report.availableOutgoingBitrate;
            statsObject.connection.bytesReceived = report.bytesReceived;
            statsObject.connection.bytesSent = report.bytesSent;
            statsObject.connection.consentRequestsSent =
                report.consentRequestsSent;
            statsObject.connection.currentRoundTripTime =
                report.currentRoundTripTime;
            statsObject.connection.localCandidateId = report.localCandidateId;
            statsObject.connection.remoteCandidateId = report.remoteCandidateId;
            statsObject.connection.requestsReceived = report.requestsReceived;
            statsObject.connection.requestsSent = report.requestsSent;
            statsObject.connection.responsesReceived = report.responsesReceived;
            statsObject.connection.responsesSent = report.responsesSent;
            statsObject.connection.timestamp = report.timestamp;
            statsObject.connection.totalRoundTripTime =
               report.totalRoundTripTime;
          }
          break;
        default:
      }
    }).bind());

    // Using the codec, local and remote candidate ID's to find the rest of the
    // relevant stats.
    stats.forEach(((report) => {
      switch (report.type) {
        case 'track':
          if (report.hasOwnProperty('trackIdentifier')) {
            if (report.trackIdentifier.indexOf(localTrackIds.video) !== 1 &
                localTrackIds.video !== '') {
              statsObject.video.local.frameHeight = report.frameHeight;
              statsObject.video.local.framesSent = report.framesSent;
              statsObject.video.local.frameWidth = report.frameWidth;
            }
            if (report.trackIdentifier.indexOf(remoteTrackIds.video) !== 1 &
                remoteTrackIds.video !== '') {
              statsObject.video.remote.frameHeight = report.frameHeight;
              statsObject.video.remote.framesDecoded = report.framesDecoded;
              statsObject.video.remote.framesDropped = report.framesDropped;
              statsObject.video.remote.framesReceived = report.framesReceived;
              statsObject.video.remote.frameWidth = report.frameWidth;
            }
            if (report.trackIdentifier.indexOf(localTrackIds.audio) !== 1 &
                localTrackIds.audio !== '') {
              statsObject.audio.local.audioLevel = report.audioLevel;
            }
            if (report.trackIdentifier.indexOf(remoteTrackIds.audio) !== 1 &
                remoteTrackIds.audio !== '') {
              statsObject.audio.remote.audioLevel = report.audioLevel;
            }
          }
          break;
        case 'codec':
          if (report.hasOwnProperty('id')) {
            if (report.id.indexOf(statsObject.audio.local.codecId) !== 1 &
                localTrackIds.audio !== '') {
              statsObject.audio.local.clockRate = report.clockRate;
              statsObject.audio.local.mimeType = report.mimeType;
              statsObject.audio.local.payloadType = report.payloadType;
            }
            if (report.id.indexOf(statsObject.audio.remote.codecId) !== 1 &
                remoteTrackIds.audio !== '') {
              statsObject.audio.remote.clockRate = report.clockRate;
              statsObject.audio.remote.mimeType = report.mimeType;
              statsObject.audio.remote.payloadType = report.payloadType;
            }
            if (report.id.indexOf(statsObject.video.local.codecId) !== 1 &
                localTrackIds.video !== '') {
              statsObject.video.local.clockRate = report.clockRate;
              statsObject.video.local.mimeType = report.mimeType;
              statsObject.video.local.payloadType = report.payloadType;
            }
            if (report.id.indexOf(statsObject.video.remote.codecId) !== 1 &
                remoteTrackIds.video !== '') {
              statsObject.video.remote.clockRate = report.clockRate;
              statsObject.video.remote.mimeType = report.mimeType;
              statsObject.video.remote.payloadType = report.payloadType;
            }
          }
          break;
        case 'local-candidate':
          if (report.hasOwnProperty('id')) {
            if (report.id.indexOf(statsObject.connection.localCandidateId) !== -1) {
              statsObject.connection.localIp = report.ip;
              statsObject.connection.localPort = report.port;
              statsObject.connection.localPriority = report.priority;
              statsObject.connection.localProtocol = report.protocol;
              statsObject.connection.localType = report.candidateType;
            }
          }
          break;
        case 'remote-candidate':
          if (report.hasOwnProperty('id')) {
            if (report.id.indexOf(statsObject.connection.remoteCandidateId) !== -1) {
              statsObject.connection.remoteIp = report.ip;
              statsObject.connection.remotePort = report.port;
              statsObject.connection.remotePriority = report.priority;
              statsObject.connection.remoteProtocol = report.protocol;
              statsObject.connection.remoteType = report.candidateType;
            }
          }
          break;
        default:
      }
    }).bind());
  }
  return statsObject;
}

function enumerateStatsFirefox(stats) {
  // Create an object structure with all the needed stats and types that we care
  // about. This allows to map the getStats stats to other stats names.
  const statsObject = {
    audio: {
      local: {
        bytesSent: 0,
        packetsSent: 0,
      },
      remote: {
        bytesReceived: -1,
        packetsReceived: 0,
        packetsLost: -1,
        jitter: 0,
        roundTripTime: 0,
        timestamp: 0,
      },
    },
    video: {
      local: {
        bytesSent: 0,
        packetsSent: -1,
        bitrateMean: 0,
        bitrateStdDev: 0,
        qpSum: 0,
      },
      remote: {
        bytesReceived: -1,
        packetsReceived: 0,
        packetsLost: -1,
        bitrateMean: 0,
        bitrateStdDev: 0,
        roundTripTime: 0,
        timestamp: 0,
      },
    },
    connection: {
      bytesSent: 0,
      bytesReceived: 0,
      timestamp: 0
    },
  };

  // Need to find the codec, local and remote ID's first.
  if (stats) {
    stats.forEach(((report, stat) => {
      //console.log('report', JSON.stringify(report));
      switch (report.type) {
        case 'outbound-rtp':
          if (report.mediaType == 'audio') {
            
            statsObject.audio.local.bytesSent = report.bytesSent;
            statsObject.audio.local.packetsSent = report.packetsSent;
          } else if (report.mediaType == 'video') {
            statsObject.video.local.bytesSent = report.bytesSent;
            statsObject.video.local.packetsSent = report.packetsSent;
            statsObject.video.local.bitrateMean = report.bitrateMean;
            statsObject.video.local.bitrateStdDev = report.bitrateStdDev;
            statsObject.video.local.qpSum = report.qpSum;
          }
        break;
        case 'remote-outbound-rtp':
          if (report.mediaType == 'audio') {
            
            statsObject.audio.local.bytesSent = report.bytesSent;
            statsObject.audio.local.packetsSent = report.packetsSent;
          } else if (report.mediaType == 'video') {
            statsObject.video.local.bytesSent = report.bytesSent;
            statsObject.video.local.packetsSent = report.packetsSent;
          }
        break;
        case 'inbound-rtp':
          if (report.mediaType == 'audio') {
            
            statsObject.audio.remote.bytesReceived = report.bytesReceived;
            statsObject.audio.remote.packetsLost = report.packetsLost;
            statsObject.audio.remote.packetsReceived = report.packetsReceived;
            statsObject.audio.remote.jitter = report.jitter;
          } else if (report.mediaType == 'video') {
            statsObject.video.remote.bytesReceived = report.bytesReceived;
            statsObject.video.remote.packetsLost = report.packetsLost;
            statsObject.video.remote.packetsReceived = report.packetsReceived;
            statsObject.video.remote.bitrateMean = report.bitrateMean;
            statsObject.video.remote.bitrateStdDev = report.bitrateStdDev;
            statsObject.video.remote.qpSum = report.qpSum;
          }
          break;
          case 'remote-inbound-rtp':
            if (report.mediaType == 'audio') {
              
              statsObject.audio.remote.bytesReceived = report.bytesReceived;
              statsObject.audio.remote.packetsLost = report.packetsLost;
              statsObject.audio.remote.packetsReceived = report.packetsReceived;
              statsObject.audio.remote.jitter = report.jitter;
              statsObject.audio.remote.roundTripTime = report.roundTripTime;
              statsObject.audio.remote.timestamp = report.timestamp;
            } else if (report.mediaType == 'video') {
              statsObject.video.remote.bytesReceived = report.bytesReceived;
              statsObject.video.remote.packetsLost = report.packetsLost;
              statsObject.video.remote.packetsReceived = report.packetsReceived;
              statsObject.video.remote.roundTripTime = report.roundTripTime;
              statsObject.video.remote.timestamp = report.timestamp;
            }
            break;
          case 'candidate-pair':
            statsObject.connection.bytesReceived = report.bytesReceived;
            statsObject.connection.bytesSent = report.bytesSent;
            statsObject.connection.timestamp = report.timestamp;
            break;
        default:
      }
    }).bind());
  }
  return statsObject;
}

function doGetUserMedia(constraints, onSuccess, onFail) {
  try {
    navigator.mediaDevices.getUserMedia(constraints)
        .then((stream) => {
          stream.onremovetrack = function () {
          };
          onSuccess(stream);
        })
        .catch((error) => {
          onFail(error);
        });
  } catch (e) {
    onFail(e.message);
  }
}
function appendSourceId(id, type, constraints) {
  if (id === '') {
    return;
  }
  if (constraints[type] === true) {
    constraints[type] = { optional: [{ sourceId: id }] };
  } else if (typeof constraints[type] === 'object') {
    if (typeof constraints[type].optional === 'undefined') {
      constraints[type].optional = [];
    }
    constraints[type].optional.push({ sourceId: id });
  }
}
function setTimeoutWithProgressBar(timeoutCallback, timeoutMs) {
  const start = window.performance.now();
  const updateProgressBar = setInterval(() => {
    const now = window.performance.now();
    setTestProgress((now - start) * 100 / timeoutMs);
  }, 100);

  const timeoutTask = function () {
    clearInterval(updateProgressBar);
    setTestProgress(100);
    timeoutCallback();
  };
  const timer = setTimeout(timeoutTask, timeoutMs);
  const finishProgressBar = function () {
    clearTimeout(timer);
    timeoutTask();
  };
  return finishProgressBar;
}

function StatisticsAggregate(rampUpThreshold) {
  this.startTime_ = 0;
  this.sum_ = 0;
  this.count_ = 0;
  this.max_ = 0;
  this.rampUpThreshold_ = rampUpThreshold;
  this.rampUpTime_ = Infinity;
}

StatisticsAggregate.prototype = {
  add(time, datapoint) {
    if (this.startTime_ === 0) {
      this.startTime_ = time;
    }
    this.sum_ += datapoint;
    this.max_ = Math.max(this.max_, datapoint);
    if (this.rampUpTime_ === Infinity &&
        datapoint > this.rampUpThreshold_) {
      this.rampUpTime_ = time;
    }
    this.count_ += 1;
  },

  getAverage() {
    if (this.count_ === 0) {
      return 0;
    }
    return Math.round(this.sum_ / this.count_);
  },

  getMax() {
    return this.max_;
  },

  getRampUpTime() {
    return Math.round(this.rampUpTime_ - this.startTime_);
  },
};

module.exports = {
 arrayAverage, arrayMax, arrayMin, enumerateStats, doGetUserMedia, setTimeoutWithProgressBar,
 setActiveTest, appendSourceId, StatisticsAggregate, enumerateStatsFirefox
};



//////////////////
// WEBPACK FOOTER
// ./src/testWebRtc/util.js
// module id = 13
// module chunks = 0